import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, ShieldAlert, Loader2, RefreshCw, Clock,
  CheckCircle2, AlertTriangle, ChevronRight, ChevronDown, FileCode, MinusCircle,
  Download,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminAuditLog
 * --------------
 * Admin-only page that surfaces the weekly leak-guard cron results.
 * Founder visibility into security posture without ever touching the
 * terminal — "last verified clean: <date>" at a glance.
 *
 * Each row is expandable: click to see the per-test pass/fail breakdown
 * grouped by test file (Iter84am — drill-down polish pass).
 */
const _OUTCOME_STYLES = {
  PASSED: { icon: CheckCircle2, dot: 'bg-emerald-400', text: 'text-emerald-300', short: '✓' },
  FAILED: { icon: AlertTriangle, dot: 'bg-rose-400', text: 'text-rose-300', short: '✗' },
  ERROR: { icon: AlertTriangle, dot: 'bg-rose-400', text: 'text-rose-300', short: '✗' },
  SKIPPED: { icon: MinusCircle, dot: 'bg-slate-500', text: 'text-slate-400', short: '↷' },
  XFAIL: { icon: MinusCircle, dot: 'bg-slate-500', text: 'text-slate-400', short: '↷' },
  XPASS: { icon: CheckCircle2, dot: 'bg-emerald-400', text: 'text-emerald-300', short: '✓' },
};

const _groupByFile = (tests) => {
  const groups = {};
  (tests || []).forEach((t) => {
    if (!groups[t.file]) groups[t.file] = [];
    groups[t.file].push(t);
  });
  return Object.entries(groups).map(([file, items]) => ({ file, items }));
};

const TestDrillDown = ({ tests }) => {
  if (!Array.isArray(tests) || tests.length === 0) {
    return (
      <div
        data-testid="audit-tests-empty"
        className="px-6 py-4 text-[12px] text-slate-400 italic"
      >
        Per-test breakdown unavailable — run a fresh audit to see each guard's outcome.
      </div>
    );
  }
  const groups = _groupByFile(tests);
  return (
    <div data-testid="audit-tests-drilldown" className="px-6 pb-4 pt-1 space-y-3">
      {groups.map((g) => (
        <div
          key={g.file}
          data-testid={`audit-tests-file-${g.file.replace(/[^\w]/g, '_')}`}
          className="rounded-lg border border-slate-800 bg-slate-950 text-slate-100"
        >
          <div className="px-3 py-2 border-b border-slate-800/60 flex items-center gap-2 flex-wrap">
            <FileCode className="w-3 h-3 text-slate-400 shrink-0" />
            <span className="font-mono text-[11px] text-slate-400">{g.file}</span>
            <span className="flex-1" />
            <span className="text-[11px] text-slate-400 tabular-nums">
              {g.items.length} test{g.items.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="divide-y divide-slate-800/40">
            {g.items.map((t, i) => {
              const s = _OUTCOME_STYLES[t.outcome] || _OUTCOME_STYLES.SKIPPED;
              const Icon = s.icon;
              return (
                <div
                  key={`${t.name}-${i}`}
                  data-testid={`audit-test-row-${t.name}`}
                  className="px-3 py-1.5 flex items-center gap-3 text-[12px] hover:bg-slate-900 transition-colors"
                >
                  <Icon className={`w-3.5 h-3.5 ${s.text} shrink-0`} />
                  <span className="font-mono text-slate-300 truncate flex-1 min-w-0">
                    {t.name}
                  </span>
                  <span className={`text-[11px] uppercase tracking-widest font-bold ${s.text} shrink-0`}>
                    {t.outcome}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminAuditLog = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [latest, setLatest] = useState(null);
  const [openRow, setOpenRow] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const _authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchRuns = async () => {
    try {
      const r = await axios.get(`${API}/admin/security-audit/recent?limit=20`, {
        headers: _authHeaders(),
      });
      setRuns(r.data?.runs || []);
      if (r.data?.runs?.length) setLatest(r.data.runs[0]);
    } catch (_e) {
      setRuns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/security-audit/run-now`, {}, {
        headers: _authHeaders(),
      });
      setLatest(r.data);
      // Auto-expand the new run so the founder lands on the breakdown.
      setOpenRow(0);
      await fetchRuns();
    } catch (_e) { /* swallow */ }
    finally { setRunning(false); }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/security-audit/export.pdf?limit=20`, {
        headers: _authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download =
        r.headers?.['x-filename'] ||
        `cartie-ai-security-audit-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) { /* swallow — toast surface optional */ }
    finally { setDownloading(false); }
  };

  useEffect(() => {
    fetchRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-slate-900 px-6 py-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-300 mr-2" />
        Loading audit history…
      </div>
    );
  }

  const allGreen = latest ? latest.all_green : null;

  return (
    <div className="min-h-screen bg-white text-slate-900 px-4 sm:px-6 py-12">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1
              data-testid="audit-log-title"
              className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-2"
            ><BrandTitle>Security Audit Log</BrandTitle></h1>
            <p className="text-sm text-slate-400 max-w-2xl">
              Weekly leak-guard cron runs every <span className="text-cyan-300 font-medium">Sunday 23:00 UTC</span>.
              You only get an email if something fails. This page is the
              full history of every public-surface verification run — click a row to drill into per-test results.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="audit-download-pdf-btn"
              onClick={handleDownloadPdf}
              disabled={downloading || runs.length === 0}
              variant="outline"
              className="h-10 bg-slate-900 hover:bg-slate-800 border-slate-700 text-slate-200"
            >
              {downloading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building…</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> PDF evidence</>
              )}
            </Button>
            <Button
              data-testid="audit-run-now-btn"
              onClick={handleRunNow}
              disabled={running}
              className="h-10 bg-emerald-500 hover:bg-cyan-400 text-slate-950 font-semibold border-0 shadow-md shadow-cyan-500/30"
            >
              {running ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" /> Run audit now</>
              )}
            </Button>
          </div>
        </div>

        {/* Status banner — vibrant green/red based on last run */}
        {latest && (
          <Card
            data-testid="audit-status-banner"
            className={`mb-6 border ${
              allGreen
                ? 'bg-slate-900 border-emerald-500/40 shadow-[0_0_40px_-12px_rgba(52,211,153,0.3)]'
                : 'bg-slate-900 border-rose-500/40 shadow-[0_0_40px_-12px_rgba(251,113,133,0.3)]'
            }`}>
            <CardContent className="p-6 flex items-center gap-4 flex-wrap">
              <div
                className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
                  allGreen
                    ? 'bg-emerald-500/20 border border-emerald-400/40'
                    : 'bg-rose-500/20 border border-rose-400/40'
                }`}
              >
                {allGreen ? (
                  <ShieldCheck className="w-7 h-7 text-emerald-300" />
                ) : (
                  <ShieldAlert className="w-7 h-7 text-rose-300" />
                )}
              </div>
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span
                    data-testid="audit-status-headline"
                    className={`text-xl font-bold ${
                      allGreen ? 'text-emerald-200' : 'text-rose-200'
                    }`}
                  >
                    {allGreen
                      ? `All ${latest.passed} guards passing`
                      : `${latest.failed} guard${latest.failed === 1 ? '' : 's'} failing`}
                  </span>
                  {allGreen ? (
                    <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/40 text-[11px] tracking-widest font-semibold">
                      ✓ ALL CLEAR
                    </Badge>
                  ) : (
                    <Badge className="bg-rose-500/20 text-rose-200 border-rose-400/40 text-[11px] tracking-widest font-semibold">
                      ⚠ ACTION REQUIRED
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  Last verified {latest.ran_at ? new Date(latest.ran_at).toLocaleString() : 'unknown'}
                  {latest.duration_seconds ? ` · ${latest.duration_seconds}s runtime` : ''}
                  {latest.email_sent ? ' · email sent' : ''}
                </p>
                {!allGreen && latest.summary && (
                  <div
                    data-testid="audit-failure-summary"
                    className="mt-3 p-3 bg-rose-950 border border-rose-500/40 rounded-md font-mono text-xs text-rose-200 leading-relaxed"
                  >
                    {latest.summary}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Run history */}
        <Card className="bg-slate-900 text-slate-100 border-slate-800">
          <CardHeader>
            <CardTitle className="text-base text-white">Run History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {runs.length === 0 ? (
              <p className="p-6 text-sm text-slate-400 text-center">
                No audit runs logged yet — click "Run audit now" to do the first one.
              </p>
            ) : (
              <div className="divide-y divide-slate-800/60">
                {runs.map((r, i) => {
                  const isOpen = openRow === i;
                  const tests = Array.isArray(r.tests) ? r.tests : [];
                  const canExpand = tests.length > 0;
                  return (
                    <div key={r.ran_at + i} data-testid={`audit-row-${i}`}>
                      <button
                        type="button"
                        data-testid={`audit-row-toggle-${i}`}
                        onClick={() => canExpand && setOpenRow(isOpen ? null : i)}
                        disabled={!canExpand}
                        className={`w-full px-6 py-3 flex items-center gap-3 flex-wrap text-left transition-colors ${
                          canExpand ? 'hover:bg-slate-900 cursor-pointer' : 'cursor-default'
                        } ${isOpen ? 'bg-slate-900' : ''}`}
                      >
                        {canExpand ? (
                          isOpen ? (
                            <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          )
                        ) : (
                          <span className="w-3.5 shrink-0" />
                        )}
                        {r.all_green ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-rose-300 shrink-0" />
                        )}
                        <span className="text-xs text-slate-400 font-mono">
                          {r.ran_at ? new Date(r.ran_at).toLocaleString() : '—'}
                        </span>
                        <span className="flex-1" />
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-emerald-300 tabular-nums">
                            ✓ {r.passed}
                          </span>
                          {r.failed > 0 && (
                            <span className="text-rose-300 tabular-nums font-bold">
                              ✗ {r.failed}
                            </span>
                          )}
                          <span className="text-slate-400 tabular-nums">
                            ↷ {r.skipped}
                          </span>
                          <span className="text-slate-400 text-[11px]">
                            {r.duration_seconds}s
                          </span>
                        </div>
                      </button>
                      {isOpen && <TestDrillDown tests={tests} />}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="mt-6 text-[11px] text-slate-400 text-center">
          Public-surface guards · 4 audit rounds · {runs.length} runs logged.
          Founder-private — never auto-published.
        </p>
      </div>
    </div>
  );
};

export default AdminAuditLog;
